import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { EmailMismatchDialogComponent } from '../components/email-mismatch-dialog/email-mismatch-dialog.component';
import { HelperService } from './helper.service';
import { UserService, MePrincipal } from './user.service';
import { Feature, UserSession } from '../models/permissions.model';

@Injectable({
    providedIn: 'root'
})
export class PermissionsService {
    private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
    private userSessionSubject = new BehaviorSubject<UserSession | null>(null);
    public userSession$ = this.userSessionSubject.asObservable();

    constructor(
        private userService: UserService,
        private msalService: MsalService,
        private helperService: HelperService,
        private dialog: MatDialog
    ) {}

    public initializeUserSession(): Observable<UserSession | null> {
        const cachedSession = this.getCachedSession();
        if (cachedSession) {
            this.userSessionSubject.next(cachedSession);
            return of(cachedSession);
        }

        return this.loadUserSession();
    }

    public loadUserSession(): Observable<UserSession | null> {
        const env = this.currentEnv();

        return this.userService.getMe(env).pipe(
            map((me: MePrincipal) => {
                // Only a Principal (staff_found, roles, tier) builds a session. The pre-change API's
                // {id, email, name} and dev's {id, oid, email, emails, name} do not: no session, no cache, no dialog.
                if (typeof me?.staff_found !== 'boolean' || !Array.isArray(me.roles) || !('tier' in me)) {
                    return null;
                }

                // Bind to the principal that came back: it must be the account active now, not the one before the call
                if (!me.oid || me.oid !== this.msalService.instance.getActiveAccount()?.localAccountId) {
                    return null;
                }

                if (me.staff_found === false) {
                    // Signed-in email not found in eCase staff: same dialog (and no session) as the old 404 branch
                    const dialogRef = this.dialog.open(EmailMismatchDialogComponent, {
                        width: '500px',
                        data: { email: me.email }
                    });

                    dialogRef.afterClosed().subscribe(refreshed => {
                        if (refreshed) {
                            // Dialog handles the refresh and reload
                            return;
                        }
                    });
                    return null;
                }

                const session: UserSession = {
                    firstName: me.first_name ?? '',
                    lastName: me.last_name ?? '',
                    roles: me.roles ?? [],
                    tier: me.tier,
                    email: me.email,
                    accountId: this.currentAccountId(),
                    env,
                    lastUpdated: Date.now()
                };
                this.cacheSession(session);
                this.userSessionSubject.next(session);
                return session;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Checks if the user's tier (computed by the API) is the given feature
     */
    public hasFeatureAccess(feature: Feature['name']): Observable<boolean> {
        return this.userSession$.pipe(
            map(session => {
                if (!session) return false;
                return session.tier === feature;
            })
        );
    }

    /**
     * Checks if the user's tier is any of the specified features
     */
    public hasAnyFeatureAccess(features: Feature['name'][]): Observable<boolean> {
        return this.userSession$.pipe(
            map(session => {
                if (!session || session.tier === null) return false;
                return features.includes(session.tier);
            })
        );
    }

    /**
     * Returns the user's tier: 'roster' (higher) or 'reallocate', as computed by the API
     */
    public getHighestFeatureAccess(): Observable<Feature['name'] | null> {
        return this.userSession$.pipe(
            map(session => session?.tier ?? null)
        );
    }

    private getCachedSession(): UserSession | null {
        const cached = localStorage.getItem('userSession');
        if (!cached) return null;

        const session: UserSession = JSON.parse(cached);
        const now = Date.now();

        // Valid only in the current shape (roles array, tier key: heals a session cached from a pre-Principal
        // reply), for the signed-in account and current eCase env, within the cache duration
        if (!session || !Array.isArray(session.roles) || !('tier' in session)
            || session.accountId !== this.currentAccountId()
            || session.env !== this.currentEnv()
            || now - session.lastUpdated > this.CACHE_DURATION) {
            localStorage.removeItem('userSession');
            return null;
        }

        return session;
    }

    private currentAccountId(): string | null {
        return this.msalService.instance.getActiveAccount()?.homeAccountId ?? null;
    }

    private currentEnv(): string {
        return this.helperService.getStoredValue('ecase_env', 'prod');
    }

    private cacheSession(session: UserSession): void {
        localStorage.setItem('userSession', JSON.stringify(session));
    }

    /** Drops the cached session (it holds the user's email): logout and account removal. */
    public clearCachedSession(): void {
        localStorage.removeItem('userSession');
    }

    public getTimeBasedGreeting(firstName: string): string {
        const hour = new Date().getHours();
        if (hour < 12) return `Good morning, ${firstName}`;
        if (hour < 17) return `Good afternoon, ${firstName}`;
        return `Good evening, ${firstName}`;
    }
}
